import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api } from "../../api/client";
import { S } from "../../styles";

export function SimulatePdfQuotePanel({
  rfqId,
  onUploaded,
}: {
  rfqId: string;
  onUploaded: () => void;
}) {
  const [selectedSupplierId, setSelectedSupplierId] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const { data: suppliersData } = useQuery({
    queryKey: ["suppliers"],
    queryFn: api.listSuppliers,
    staleTime: 5 * 60_000,
  });
  const suppliers = suppliersData?.items ?? [];
  const supplierId = selectedSupplierId || suppliers[0]?.id || "";

  const uploadMutation = useMutation({
    mutationFn: () => api.uploadPdfQuote(rfqId, supplierId, file!),
    // Only refresh the grid when a quote actually landed in it.
    onSuccess: (result) => {
      if (result.accepted) onUploaded();
    },
  });

  const methodLabel: Record<string, string> = {
    "tika+vlm": "Text PDF — Tika extracted, VLM parsed",
    vlm: "Scanned / image PDF — VLM vision",
  };

  const confidenceColor = (c: number) =>
    c >= 0.8 ? "#2f9e44" : c >= 0.5 ? "#e67700" : "#c92a2a";

  const result = uploadMutation.data;

  return (
    <div
      style={{ marginTop: 14, paddingTop: 12, borderTop: "1px dashed #e0e4f0" }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase" as const,
          color: "#aaa",
          letterSpacing: 0.5,
          marginBottom: 10,
        }}
      >
        Simulate PDF Quote (Channel B — Dev / Test)
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "flex-end",
          flexWrap: "wrap" as const,
        }}
      >
        <div>
          <label style={{ ...S.label, fontSize: 12 }}>Supplier</label>
          <select
            style={{ ...S.select, marginBottom: 0, fontSize: 12, width: 180 }}
            value={supplierId}
            onChange={(e) => setSelectedSupplierId(e.target.value)}
          >
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ ...S.label, fontSize: 12 }}>Quote PDF / Image</label>
          <input
            type="file"
            accept="application/pdf,image/png,image/jpeg"
            style={{ fontSize: 12 }}
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              uploadMutation.reset();
            }}
          />
        </div>
        <button
          style={{
            ...S.btnSm,
            background: "#7c3aed",
            opacity: !file || !supplierId || uploadMutation.isPending ? 0.5 : 1,
            fontSize: 12,
          }}
          onClick={() => uploadMutation.mutate()}
          disabled={!file || !supplierId || uploadMutation.isPending}
        >
          {uploadMutation.isPending ? "Extracting…" : "📄 Upload & Extract"}
        </button>
      </div>

      {uploadMutation.error && (
        <p style={{ ...S.err, marginTop: 8, fontSize: 12 }}>
          ⚠ {uploadMutation.error.message}
          <span style={{ ...S.muted, display: "block" }}>
            Check that VITE_INTERNAL_API_TOKEN is set and Tika is running.
          </span>
        </p>
      )}

      {result && (
        <div
          style={{
            marginTop: 10,
            padding: "10px 14px",
            // A document the pipeline read but could not use is an amber
            // verdict, not a red failure: the extraction worked, the page just
            // was not a quote. Only a broken request gets the error styling
            // above.
            background: result.accepted ? "#f5f0ff" : "#fff9e6",
            border: `1px solid ${result.accepted ? "#7c3aed44" : "#e6790044"}`,
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          <div
            style={{
              fontWeight: 700,
              color: result.accepted ? "#7c3aed" : "#a15c00",
              marginBottom: 6,
            }}
          >
            {result.accepted ? "✓" : "⚠"} {result.message}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "3px 12px",
              color: "#555",
            }}
          >
            <span style={{ color: "#888" }}>Method</span>
            <span>
              {methodLabel[result.extraction_method] ??
                result.extraction_method}
            </span>
            <span style={{ color: "#888" }}>Confidence</span>
            <span
              style={{
                fontWeight: 700,
                color: confidenceColor(result.confidence),
              }}
            >
              {Math.round(result.confidence * 100)}%
            </span>
            {result.part_number && (
              <>
                <span style={{ color: "#888" }}>Part #</span>
                <span style={{ fontFamily: "monospace" }}>
                  {result.part_number}
                </span>
              </>
            )}
            {result.unit_price !== null && result.unit_price !== undefined && (
              <>
                <span style={{ color: "#888" }}>Unit Price</span>
                <span style={{ fontWeight: 600 }}>
                  USD {result.unit_price.toFixed(2)}
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
